import Big from "big.js";
import { BudgetDTO } from "../../../dto/budget/BudgetDTO";
import { BudgetDetailDTO, calculateDistributionPercentages, distributeByPercentage } from "../../../dto/budget/BudgetDetailDTO";
import { BillableItemDTO } from "../../../dto/budget/BilliableItemDTO";
import { ProfessionalAssignmentDTO } from "../../../dto/budget/ProfessionalAssignmentDTO";
import { BillableItemStatus, getBillableItemStatusDescription } from "../../../enums/BillableItemStatus";
import { sumMoney } from "../../../utils/money";

/** Categorías de ítems de la facturación sanatorial, en el orden en que se muestran. Equipamiento no se factura acá. */
export type BillItemCategory =
    | 'professionalActivities'
    | 'disposables'
    | 'hospitalizationDays'
    | 'nutritionalSupportItems'
    | 'medicines'
    | 'extras';

export const BILL_ITEM_CATEGORIES: BillItemCategory[] = [
    'professionalActivities',
    'disposables',
    'hospitalizationDays',
    'nutritionalSupportItems',
    'medicines',
    'extras',
];

export interface BillItemCategoryConfig {
    title: string;
    /** Cabecera de la columna del ítem. */
    itemHeader: string;
    quantityHeader: string;
    /** Nombre del ítem en singular, para "Agregar …" y "Elegí …". */
    itemLabel: string;
    /** Artículo indefinido del ítem ("un descartable", "una práctica"). */
    article: 'un' | 'una';
    emptyMessage: string;
}

export const BILL_ITEM_CATEGORY_CONFIG: Record<BillItemCategory, BillItemCategoryConfig> = {
    professionalActivities: { title: 'Actividad profesional', itemHeader: 'Práctica', quantityHeader: 'Unidades', itemLabel: 'práctica', article: 'una', emptyMessage: 'Sin prácticas cargadas' },
    disposables: { title: 'Descartables', itemHeader: 'Ítem', quantityHeader: 'Unidades', itemLabel: 'descartable', article: 'un', emptyMessage: 'Sin descartables cargados' },
    hospitalizationDays: { title: 'Días de internación', itemHeader: 'Estadía', quantityHeader: 'Días', itemLabel: 'estadía', article: 'una', emptyMessage: 'Sin días de internación cargados' },
    nutritionalSupportItems: { title: 'Soporte nutricional', itemHeader: 'Fórmula', quantityHeader: 'Unidades', itemLabel: 'fórmula', article: 'una', emptyMessage: 'Sin fórmulas cargadas' },
    medicines: { title: 'Medicamentos', itemHeader: 'Medicamento', quantityHeader: 'Unidades', itemLabel: 'medicamento', article: 'un', emptyMessage: 'Sin medicamentos cargados' },
    extras: { title: 'Extras', itemHeader: 'Ítem', quantityHeader: 'Unidades', itemLabel: 'extra', article: 'un', emptyMessage: 'Sin extras cargados' },
};

/** Tipo de detalle que manda el backend para cada categoría (getBudgetDetailByPatient). */
export const DETAIL_TYPES: Record<BillItemCategory, number[]> = {
    professionalActivities: [2],
    disposables: [3, 9],               // insumo de enfermería, insumo de quirófano
    hospitalizationDays: [10],
    nutritionalSupportItems: [5],
    medicines: [6, 8],                 // medicamento de internación, medicamento de quirófano
    extras: [],
};

export type BillItems = Pick<BudgetDTO, BillItemCategory>;

// ── Unidades ──
// Pueden tener decimales: la medicación se puede cobrar en proporción a la dosis (10 UI de una lapicera de 300 UI son
// 0,033333). Se suman con big.js y quedan a 6 decimales (HALF_UP), como en el back.

/** Decimales de las unidades, en la línea y en cada billable item, como en el back. */
const UNITS_DECIMALS = 6;

/** Unidades a 6 decimales (HALF_UP, como el back); las negativas cuentan como 0. */
const roundUnits = (units: Big): number => (units.gt(0) ? units : new Big(0)).round(UNITS_DECIMALS, Big.roundHalfUp).toNumber();

/** Suma de las cantidades de los billable items (las vacías cuentan como 0). */
const sumQuantities = (billableItems: BillableItemDTO[]): Big =>
    billableItems.reduce((sum, bi) => sum.plus(Number(bi.quantity) || 0), new Big(0));

const unitsQtyOf = (item: BudgetDetailDTO): Big => new Big(Number(item.unitsqty ?? 0) || 0);

export const hasBillableItems = (item: BudgetDetailDTO): boolean => (item.billableitems?.length ?? 0) > 0;

/** Unidades que se facturan: las facturables de los billable items o, si no tiene, unitsqty. */
export const getBillableUnits = (item: BudgetDetailDTO): number => roundUnits(hasBillableItems(item)
    ? sumQuantities((item.billableitems ?? []).filter(bi => bi.status === BillableItemStatus.FACTURABLE))
    : unitsQtyOf(item));

/** Unidades cargadas en cualquier estado. */
export const getTotalUnits = (item: BudgetDetailDTO): number =>
    roundUnits(hasBillableItems(item) ? sumQuantities(item.billableitems ?? []) : unitsQtyOf(item));

export const getUnitsByStatus = (item: BudgetDetailDTO, status: BillableItemStatus): number =>
    roundUnits(sumQuantities((item.billableitems ?? []).filter(bi => bi.status === status)));

/** Unidades de medicamentos y soporte nutricional cargadas en pantalla: hasta 6 decimales. */
export const toUnits = (value: unknown): number => roundUnits(new Big(Number(value ?? 0) || 0));

/** Días, prácticas, descartables y extras se cargan enteros. */
export const toWholeUnits = (value: unknown): number => Math.max(0, Math.trunc(Number(value ?? 0)) || 0);

// Sin ceros de más ni separador de miles: los enteros se ven como siempre.
const UNITS_FORMAT = new Intl.NumberFormat('es-AR', { maximumFractionDigits: UNITS_DECIMALS, useGrouping: false });

/** Unidades (de una línea o de un billable item) para mostrar, hasta 6 decimales. */
export const formatUnits = (units: number): string => UNITS_FORMAT.format(units);

/** Texto de la celda de unidades ("3" o "2 de 3") y su tooltip con el detalle por estado. */
export function getUnitsLabel(item: BudgetDetailDTO): { text: string; detail: string } {
    const billable = getBillableUnits(item);
    const total = getTotalUnits(item);
    const parts = [`${formatUnits(billable)} ${billable === 1 ? 'facturable' : 'facturables'}`];
    const bonified = getUnitsByStatus(item, BillableItemStatus.FACTURABLE_BONIFICADO);
    const notBillable = getUnitsByStatus(item, BillableItemStatus.NO_FACTURABLE);
    if (bonified) parts.push(`${formatUnits(bonified)} ${bonified === 1 ? 'bonificada' : 'bonificadas'}`);
    if (notBillable) parts.push(`${formatUnits(notBillable)} no ${notBillable === 1 ? 'facturable' : 'facturables'}`);
    return { text: billable === total ? formatUnits(billable) : `${formatUnits(billable)} de ${formatUnits(total)}`, detail: parts.join(', ') };
}

// ── Importes ──
// Las líneas guardadas llegan del back con su importe (valuemonth). El front calcula, con big.js, solo lo que se
// agrega o se cambia en pantalla, con la misma regla que el back: unidades facturables (a 6 decimales) × precio, a 2
// decimales.

/** Importe de unidades × precio, a 2 decimales (HALF_UP, como el back). */
export const amountOf = (units: number, unitprice: number): Big =>
    new Big(units).times(unitprice).round(2, Big.roundHalfUp);

/** Importe de la línea: unidades facturables × valor unitario. La facturación sanatorial no maneja ganancia. */
export const lineValue = (item: BudgetDetailDTO): Big => amountOf(getBillableUnits(item), Number(item.unitprice) || 0);

/** La línea con su importe, para lo que se agrega o se cambia en pantalla. */
export const withLineValue = <T extends BudgetDetailDTO>(item: T): T => ({ ...item, valuemonth: lineValue(item).toNumber() });

/** Sin ganancia: si el ítem trae el campo (descartables, soporte, medicamentos), se guarda en 0. */
const withoutEarnings = <T extends BudgetDetailDTO>(item: T): T =>
    'earnings' in item ? { ...item, earnings: 0 } : item;

/** Recalcula unidades e importe de un ítem que se cambió en pantalla (Extras no pisa unitsqty). */
export function recalculateItem<T extends BudgetDetailDTO>(category: BillItemCategory, item: T): T {
    const clean = withoutEarnings(item);
    return withLineValue(category === 'extras' ? clean : { ...clean, unitsqty: getBillableUnits(clean) });
}

/** Subtotal de una lista: la suma, con big.js, de los importes de sus líneas. */
export const getCategorySubtotal = (items: BudgetDetailDTO[]): Big => sumMoney(items.map(item => item.valuemonth));

/** Total de la facturación: la suma de los subtotales. */
export const getBudgetTotal = (budget: BillItems): Big =>
    BILL_ITEM_CATEGORIES.reduce((total, category) => total.plus(getCategorySubtotal(budget[category] ?? [])), new Big(0));

/** Deja unidades, importes y total listos para guardar. */
export function normalizeBudgetItems(budget: BudgetDTO): BudgetDTO {
    const next: BudgetDTO = { ...budget };
    BILL_ITEM_CATEGORIES.forEach(category => {
        const items = (budget[category] ?? []) as BudgetDetailDTO[];
        (next[category] as BudgetDetailDTO[]) = items.map(item => recalculateItem(category, item));
    });
    next.total = getBudgetTotal(next).toNumber();
    return next;
}

/**
 * Proporción entre honorarios y derechos de una práctica. El back no manda los porcentajes: en las líneas guardadas
 * sale de sus importes, como en el panel de edición (sin importes, todo va a honorarios).
 */
const distributionOf = (item: BudgetDetailDTO) =>
    item.medicalpercentage != null && item.expensespercentage != null
        ? { medicalPercentage: item.medicalpercentage, expensesPercentage: item.expensespercentage }
        : calculateDistributionPercentages(Number(item.medicalamount) || 0, Number(item.expensesamount) || 0);

/** Nuevo valor unitario, manteniendo cómo se reparte entre honorarios y derechos en cada categoría. */
export function withUnitPrice<T extends BudgetDetailDTO>(category: BillItemCategory, item: T, unitprice: number): T {
    if (category === 'professionalActivities') {
        const { medicalPercentage, expensesPercentage } = distributionOf(item);
        const { medicalamount, expensesamount } = distributeByPercentage(unitprice, medicalPercentage, expensesPercentage);
        return { ...item, unitprice, medicalamount, expensesamount, medicalpercentage: medicalPercentage, expensespercentage: expensesPercentage };
    }
    if (category === 'hospitalizationDays') return { ...item, unitprice, ...allExpenses(unitprice) };
    return { ...item, unitprice };
}

/** Los días de internación son todo derechos: honorarios y derechos son solo de las prácticas. */
export const allExpenses = (amount: number) => ({
    medicalamount: 0,
    expensesamount: amount,
    medicalpercentage: 0,
    expensespercentage: 1,
});

export const withCoverage = <T extends BudgetDetailDTO>(item: T, healthEntityPercentage: number): T => {
    const value = Math.min(100, Math.max(0, healthEntityPercentage));
    return { ...item, healthentitypercentagecoverage: value, patientpercentagecoverage: 100 - value };
};

export const withBillableStatus = <T extends BudgetDetailDTO>(item: T, unitIndex: number, status: BillableItemStatus): T => ({
    ...item,
    billableitems: (item.billableitems ?? []).map((bi, i) => i === unitIndex ? { ...bi, status, statusDescription: getBillableItemStatusDescription(status) } : bi),
});

export const withAssignments = <T extends BudgetDetailDTO>(item: T, unitIndex: number, assignments: ProfessionalAssignmentDTO[]): T => ({
    ...item,
    billableitems: (item.billableitems ?? []).map((bi, i) => i === unitIndex ? { ...bi, professionalassignmentdtolist: assignments } : bi),
});

export const newBillableItem = (quantity: number, extra?: Partial<BillableItemDTO>): BillableItemDTO => ({
    status: BillableItemStatus.FACTURABLE,
    quantity,
    itemid: null as unknown as number, // el backend asigna el id al guardar (igual que las tablas anteriores)
    professionalassignmentdtolist: [],
    statusDescription: getBillableItemStatusDescription(BillableItemStatus.FACTURABLE),
    healthpatientservice: null,
    ...extra,
});

// ── Servicios del paciente duplicados ──

export const getFirstHealthPatientService = (item: BudgetDetailDTO): number | null =>
    item.billableitems?.find(bi => bi.healthpatientservice !== null && bi.healthpatientservice !== undefined)?.healthpatientservice ?? null;

/** healthpatientservice que aparece en más de un billable item de la categoría, con la cantidad de apariciones. */
export function getDuplicatedHealthPatientServices(items: BudgetDetailDTO[]): Map<number, number> {
    const count = new Map<number, number>();
    items.forEach(item => (item.billableitems ?? []).forEach(bi => {
        if (bi.healthpatientservice !== null && bi.healthpatientservice !== undefined) {
            count.set(bi.healthpatientservice, (count.get(bi.healthpatientservice) ?? 0) + 1);
        }
    }));
    const duplicated = new Map<number, number>();
    count.forEach((n, hps) => { if (n > 1) duplicated.set(hps, n); });
    return duplicated;
}

const getItemDuplicatedServices = (item: BudgetDetailDTO, duplicated: Map<number, number>): { hps: number; count: number }[] => {
    const result: { hps: number; count: number }[] = [];
    (item.billableitems ?? []).forEach(bi => {
        const hps = bi.healthpatientservice;
        if (hps === null || hps === undefined) return;
        const n = duplicated.get(hps);
        if (n && !result.some(d => d.hps === hps)) result.push({ hps, count: n });
    });
    return result;
};

/** Filas con su índice original; primero las que tienen servicio del paciente, agrupadas por servicio. */
export function sortByHealthPatientService<T extends BudgetDetailDTO>(items: T[]): { item: T; index: number }[] {
    return items
        .map((item, index) => ({ item, index }))
        .sort((a, b) => {
            const aHps = getFirstHealthPatientService(a.item);
            const bHps = getFirstHealthPatientService(b.item);
            if (aHps !== null && bHps === null) return -1;
            if (aHps === null && bHps !== null) return 1;
            if (aHps !== null && bHps !== null) return aHps - bHps;
            return 0;
        });
}

// ── Distribución de honorarios ──

export const getAssignmentsTotal = (assignments?: ProfessionalAssignmentDTO[]): number =>
    (assignments ?? []).reduce((sum, a) => sum + (Number(a.percentage) || 0), 0);

export const isFullyDistributed = (total: number): boolean => Math.abs(total - 100) < 0.005;

export const roundTwo = (n: number): number => Math.round(n * 100) / 100;

export const formatPercent = (n: number): string =>
    `${new Intl.NumberFormat('es-AR', { maximumFractionDigits: 2 }).format(roundTwo(n))}%`;

export type DistributionState = { kind: 'empty' } | { kind: 'complete'; professionals: number } | { kind: 'partial'; total: number };

export function getDistributionState(unit: BillableItemDTO): DistributionState {
    const assignments = unit.professionalassignmentdtolist ?? [];
    if (assignments.length === 0) return { kind: 'empty' };
    const total = getAssignmentsTotal(assignments);
    return isFullyDistributed(total) ? { kind: 'complete', professionals: assignments.length } : { kind: 'partial', total };
}

/** Primer problema de distribución de una práctica (solo unidades con profesionales), para avisar al aplicar. */
export function findDistributionError(item: BudgetDetailDTO): { unitIndex: number; message: string } | null {
    const units = item.billableitems ?? [];
    for (let i = 0; i < units.length; i++) {
        const state = getDistributionState(units[i]);
        if (state.kind !== 'partial') continue;
        const where = units.length > 1 ? ` en la unidad ${i + 1}` : '';
        return state.total < 100
            ? { unitIndex: i, message: `Completá la distribución: faltan ${formatPercent(100 - state.total)}${where}` }
            : { unitIndex: i, message: `Revisá la distribución: sobran ${formatPercent(state.total - 100)}${where}` };
    }
    return null;
}

// ── Avisos de la columna Estado ──

export interface ItemWarning {
    label: string;
    detail: string;
    severity: 'warn' | 'err';
}

export function getItemWarnings(category: BillItemCategory, item: BudgetDetailDTO, duplicated: Map<number, number>): ItemWarning[] {
    const warnings: ItemWarning[] = getItemDuplicatedServices(item, duplicated).map(({ hps, count }) => ({
        label: 'Servicio duplicado',
        detail: `El servicio del paciente ${hps} está en ${count} ítems. Revisá antes de facturar.`,
        severity: 'warn',
    }));
    if (category !== 'professionalActivities') return warnings;

    const billableUnits = (item.billableitems ?? []).filter(bi => bi.status === BillableItemStatus.FACTURABLE);
    if (billableUnits.some(bi => (bi.professionalassignmentdtolist ?? []).length === 0)) {
        warnings.push({ label: 'Sin distribución', detail: 'Hay unidades facturables sin profesionales asignados', severity: 'warn' });
    } else {
        const partial = billableUnits.map(getDistributionState).find(s => s.kind === 'partial');
        if (partial && partial.kind === 'partial') {
            warnings.push({
                label: `Distribución ${formatPercent(partial.total)}`,
                detail: 'La distribución de honorarios tiene que sumar 100%',
                severity: partial.total > 100 ? 'err' : 'warn',
            });
        }
    }
    return warnings;
}

// ── Días de internación ──

const pad2 = (n: number) => String(n).padStart(2, '0');

/** Fecha del período de estadía como 'YYYY-MM-DD'. Del backend llega como string ISO o como [año, mes, día]. */
export function toIsoDate(value: unknown): string | null {
    if (!value) return null;
    if (Array.isArray(value) && value.length >= 3) return `${value[0]}-${pad2(Number(value[1]))}-${pad2(Number(value[2]))}`;
    if (value instanceof Date) return isNaN(value.getTime()) ? null : `${value.getFullYear()}-${pad2(value.getMonth() + 1)}-${pad2(value.getDate())}`;
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) return value.substring(0, 10);
    return null;
}

/** Fin (exclusivo) del período: la función de facturación cuenta los días como dateto - datefrom. */
export function addDays(isoDate: string, days: number): string {
    const d = new Date(isoDate + 'T00:00:00');
    d.setDate(d.getDate() + days);
    return toIsoDate(d) as string;
}

/** Los días se editan en la fila cuando la estadía es un único período facturable. */
export const canEditStayDays = (item: BudgetDetailDTO): boolean => {
    const billable = item.billableitems ?? [];
    return billable.length === 1
        && billable[0].status === BillableItemStatus.FACTURABLE
        && toIsoDate(billable[0].datefrom) !== null;
};

export function withStayDays<T extends BudgetDetailDTO>(item: T, days: number): T {
    const billable = item.billableitems![0];
    const from = toIsoDate(billable.datefrom) as string;
    return { ...item, unitsqty: days, billableitems: [{ ...billable, quantity: days, datefrom: from, dateto: addDays(from, days) }] };
}

/** La cantidad se edita en la fila si no viene de unidades registradas (o si es una estadía de un solo período). */
export const canEditQuantity = (category: BillItemCategory, item: BudgetDetailDTO): boolean => {
    if (category === 'professionalActivities') return false;
    if (category === 'hospitalizationDays') return canEditStayDays(item);
    return !hasBillableItems(item);
};

/** Cantidad cambiada en la fila: los días van enteros (al menos 1); el resto, hasta 6 decimales. */
export function withQuantity<T extends BudgetDetailDTO>(category: BillItemCategory, item: T, quantity: number): T {
    if (category === 'hospitalizationDays') return withStayDays(item, Math.max(1, toWholeUnits(quantity)));
    return { ...item, unitsqty: toUnits(quantity) };
}
